import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { KeyRound, Plus, Trash2, UserX, UserCheck } from 'lucide-react';
import { api, type TenantDetail } from '../api';
import { authClient } from '../auth-client';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Skeleton } from '../components/ui/skeleton';
import { EmptyState, ErrorNote, Field, PageHeader, Panel } from '../components/kit';

const SELECT =
  'h-8 rounded-[5px] border border-rule bg-field px-2.5 text-[13px] text-ink outline-none transition-[border-color,box-shadow] hover:border-rule-strong focus-visible:border-brand focus-visible:ring-2 focus-visible:ring-brand/25';

/** Accounts for the whole installation. Tenant access is granted separately. */
export function UsersPage() {
  const client = useQueryClient();
  const [inviting, setInviting] = useState(false);
  const [resetting, setResetting] = useState<{ id: string; email: string } | null>(null);
  const users = useQuery({
    queryKey: ['users'],
    queryFn: async () => {
      const result = await authClient.admin.listUsers({ query: { limit: 100 } });
      if (result.error) throw new Error(result.error.message ?? 'could not list users');
      return result.data?.users ?? [];
    },
  });
  const memberships = useQuery({
    queryKey: ['memberships'],
    queryFn: () =>
      api.get<Record<string, { tenantId: string; tenant: string; role: string }[]>>(
        '/api/users/memberships',
      ),
  });
  const refresh = () => {
    client.invalidateQueries({ queryKey: ['users'] });
    client.invalidateQueries({ queryKey: ['memberships'] });
  };

  const setBanned = useMutation({
    mutationFn: async ({ id, banned }: { id: string; banned: boolean }) =>
      banned ? authClient.admin.banUser({ userId: id }) : authClient.admin.unbanUser({ userId: id }),
    onSuccess: refresh,
  });
  const remove = useMutation({
    mutationFn: async (id: string) => authClient.admin.removeUser({ userId: id }),
    onSuccess: refresh,
  });

  return (
    <>
      <PageHeader
        title="Users"
        description="Every account that can sign in. Accounts are made by invitation: the person picks their own password when they accept."
        actions={
          <Button onClick={() => setInviting(true)}>
            <Plus className="size-3.5" aria-hidden /> Invite somebody
          </Button>
        }
      />

      <Panel title="Accounts" description="Administrators manage users and tenants; everyone else works inside a tenant.">
        {users.isPending ? (
          <Skeleton className="h-24 w-full" />
        ) : (users.data ?? []).length === 0 ? (
          <EmptyState title="No accounts yet" />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Email</TableHead>
                <TableHead className="hidden md:table-cell">Name</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Tenants</TableHead>
                <TableHead>State</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(users.data ?? []).map((user) => (
                <TableRow key={user.id}>
                  <TableCell className="data font-semibold text-ink">{user.email}</TableCell>
                  <TableCell className="hidden md:table-cell">{user.name}</TableCell>
                  <TableCell>
                    <Badge variant={user.role === 'admin' ? 'default' : 'secondary'}>{user.role ?? 'user'}</Badge>
                  </TableCell>
                  <TableCell className="max-w-[20rem]">
                    {/* An installation administrator reaches every tenant whether
                        or not a membership row says so; saying "every tenant" is
                        the truth, where listing rows would not be. */}
                    {user.role === 'admin' ? (
                      <span className="text-[12px] text-ink-2">every tenant</span>
                    ) : (memberships.data?.[user.id] ?? []).length === 0 ? (
                      <span className="text-[12px] text-ink-3">none — sees nothing yet</span>
                    ) : (
                      <div className="flex flex-wrap gap-1">
                        {(memberships.data?.[user.id] ?? []).map((membership) => (
                          <Badge
                            key={membership.tenantId}
                            variant="secondary"
                            title={`${membership.role} in ${membership.tenant}`}
                          >
                            {membership.tenant} · {membership.role}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant={user.banned ? 'destructive' : 'ok'}>
                      {user.banned ? 'disabled' : 'active'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setBanned.mutate({ id: user.id, banned: !user.banned })}
                      >
                        {user.banned ? (
                          <UserCheck className="size-3.5" aria-hidden />
                        ) : (
                          <UserX className="size-3.5" aria-hidden />
                        )}
                        <span className="sr-only">{user.banned ? 'Enable' : 'Disable'} {user.email}</span>
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setResetting({ id: user.id, email: user.email })}
                      >
                        <KeyRound className="size-3.5" aria-hidden />
                        <span className="sr-only">Set a new password for {user.email}</span>
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => remove.mutate(user.id)}>
                        <Trash2 className="size-3.5" aria-hidden />
                        <span className="sr-only">Delete {user.email}</span>
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        <ErrorNote error={users.error ?? setBanned.error ?? remove.error} />
      </Panel>

      <InviteDialog open={inviting} onClose={() => setInviting(false)} onInvited={refresh} />
      <ResetPasswordDialog target={resetting} onClose={() => setResetting(null)} />
    </>
  );
}

/**
 * Until mail is wired up, an administrator setting a password is the recovery
 * path — better than an account nobody can get back into.
 */
function ResetPasswordDialog({
  target,
  onClose,
}: {
  target: { id: string; email: string } | null;
  onClose: () => void;
}) {
  const [password, setPassword] = useState('');
  const reset = useMutation({
    mutationFn: async () => {
      const result = await authClient.admin.setUserPassword({ userId: target!.id, newPassword: password });
      if (result.error) throw new Error(result.error.message ?? 'could not set the password');
      return result.data;
    },
    onSuccess: () => {
      setPassword('');
      onClose();
    },
  });

  return (
    <Dialog open={target !== null} onOpenChange={(next) => !next && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Set a new password</DialogTitle>
          <DialogDescription>
            For {target?.email}. Tell them the new password yourself — no email is sent. Their existing sessions
            stay valid until they expire.
          </DialogDescription>
        </DialogHeader>
        <form
          className="grid gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            reset.mutate();
          }}
        >
          <div className="grid content-start gap-1.5">
            <Label htmlFor="reset-password">New password</Label>
            <Input
              id="reset-password"
              type="password"
              required
              minLength={12}
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
          </div>
          <ErrorNote error={reset.error} />
          <div>
            <Button type="submit" disabled={reset.isPending}>
              Set password
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Inviting is how accounts are made now: the person sets their own password when
 * they accept, so nobody ever relays one. It needs a tenant, because an account
 * with no tenant sees nothing — which is why "create the account, grant access
 * later" was two steps that only ever happened together.
 */
function InviteDialog({
  open,
  onClose,
  onInvited,
}: {
  open: boolean;
  onClose: () => void;
  onInvited: () => void;
}) {
  const tenants = useQuery({
    queryKey: ['tenants'],
    queryFn: () => api.get<TenantDetail[]>('/api/tenants'),
    enabled: open,
  });
  const [form, setForm] = useState({ email: '', tenantId: '', role: 'member' });
  const [notice, setNotice] = useState('');
  const tenantId = form.tenantId || tenants.data?.[0]?.id || '';

  const invite = useMutation({
    mutationFn: () =>
      api.post<{ outcome: 'granted' | 'role-changed' | 'invited'; email: string; acceptUrl?: string; emailed?: boolean }>(
        `/api/tenants/${tenantId}/members`,
        { email: form.email, role: form.role },
      ),
    onSuccess: (result) => {
      setNotice(
        result.outcome === 'invited'
          ? result.emailed
            ? `Invitation emailed to ${result.email}. The link, in case it does not arrive: ${result.acceptUrl}`
            : `Not emailed — send this invitation link yourself: ${result.acceptUrl}`
          : `${result.email} already had an account and now has access.`,
      );
      setForm({ email: '', tenantId, role: form.role });
      onInvited();
    },
  });

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Invite somebody</DialogTitle>
          <DialogDescription>
            They get a link, choose their own password, and land in the tenant you pick. An address that
            already has an account is simply given access.
          </DialogDescription>
        </DialogHeader>
        {tenants.isPending ? (
          <Skeleton className="h-24 w-full" />
        ) : (tenants.data ?? []).length === 0 ? (
          <EmptyState
            title="No tenants yet"
            hint="An invitation grants access to a tenant, so there has to be one first. Create it under Tenants."
          />
        ) : (
          <form
            className="grid gap-4"
            onSubmit={(event) => {
              event.preventDefault();
              setNotice('');
              invite.mutate();
            }}
          >
            <Field label="Email" htmlFor="invite-email">
              <Input
                id="invite-email"
                type="email"
                required
                autoFocus
                placeholder="colleague@example.com"
                value={form.email}
                onChange={(event) => setForm({ ...form, email: event.target.value })}
              />
            </Field>
            <Field label="Tenant" htmlFor="invite-tenant" hint="What they will be able to see and run in.">
              <select
                id="invite-tenant"
                value={tenantId}
                onChange={(event) => setForm({ ...form, tenantId: event.target.value })}
                className={SELECT}
              >
                {(tenants.data ?? []).map((tenant) => (
                  <option key={tenant.id} value={tenant.id}>
                    {tenant.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Role in that tenant" htmlFor="invite-role">
              <select
                id="invite-role"
                value={form.role}
                onChange={(event) => setForm({ ...form, role: event.target.value })}
                className={SELECT}
              >
                <option value="viewer">viewer — read only</option>
                <option value="member">member — queue and run</option>
                <option value="admin">admin — configure the tenant</option>
              </select>
            </Field>
            {notice && <p className="data break-all text-ink-2">{notice}</p>}
            <ErrorNote error={invite.error} />
            <div className="flex justify-end gap-2 border-t border-rule pt-3">
              <Button type="button" variant="outline" onClick={onClose}>
                {notice ? 'Done' : 'Cancel'}
              </Button>
              <Button type="submit" disabled={invite.isPending || !form.email.trim()}>
                Send invitation
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
